/**
 * TBB Collector Profile — JS hydration
 *
 * Mirrors tbb-seller-profile.js in spirit but adapted for the buyer-side
 * collector identity surface. Reads handle from `?u=` URL param, fetches
 * /collector/profile from the CF worker, hydrates skeleton mount points
 * in s_collector-profile-header.liquid + s_collector-picks.liquid.
 *
 * Picks hydration: worker returns array of product handles; this script
 * fetches /products/<handle>.js (Shopify storefront, public) per handle
 * to populate the picks grid with title/image/price/url.
 *
 * No dependencies. Idempotent (data-tbb-collector-init flag).
 */
(function () {
  'use strict';

  /* -------------- helpers -------------- */

  function formatCount(n) {
    if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    return String(n);
  }

  /**
   * Validate / coerce a URL value before assigning to anchor.href.
   * Rejects javascript:/data:/vbscript: schemes. For web URLs prepends
   * https:// when stored as a bare domain. Returns "" when unsafe so
   * the chip stays hidden.
   */
  function safeUrl(value, scheme) {
    if (!value || typeof value !== 'string') return '';
    var v = value.trim();
    if (!v) return '';
    if (/^\s*(javascript|data|vbscript):/i.test(v)) return '';
    if (scheme === 'mailto') {
      if (!/^[^\s@<>"']+@[^\s@<>"']+\.[^\s@<>"']+$/.test(v)) return '';
      return 'mailto:' + encodeURIComponent(v);
    }
    if (!/^https?:\/\//i.test(v)) v = 'https://' + v;
    try { new URL(v); return v; } catch (e) { return ''; }
  }

  function setContactChip(container, attr, safeHref) {
    var el = container.querySelector('[' + attr + ']');
    if (!el || !safeHref) return;
    el.href = safeHref;
    el.removeAttribute('hidden');
  }

  /**
   * Pull collector handle from URL (?u=lemarc OR ?handle=lemarc).
   * Returns null if missing or malformed.
   */
  function getCollectorHandle() {
    try {
      var params = new URLSearchParams(window.location.search);
      var raw = (params.get('u') || params.get('handle') || '').toLowerCase().trim();
      if (!raw) return null;
      // Same character class as the worker's regex — tighter validation
      // happens server-side; this is just a defensive client guard.
      if (!/^[a-z0-9][a-z0-9\-_]{0,30}$/.test(raw)) return null;
      return raw;
    } catch (e) {
      return null;
    }
  }

  /**
   * Render preset-tag chips (90s baby, Y2K kid, etc.). Empty array hides
   * the container entirely so the row collapses.
   */
  function renderPresetTags(container, tags) {
    if (!container) return;
    if (!Array.isArray(tags) || tags.length === 0) return;
    container.textContent = '';
    for (var i = 0; i < tags.length; i++) {
      var chip = document.createElement('span');
      chip.className = 'tbb-collector-tag-chip';
      chip.textContent = tags[i];
      container.appendChild(chip);
    }
    container.removeAttribute('hidden');
  }

  /**
   * Render the 404 / not-found state — wipes the rest of the card and
   * surfaces the empty-state CTA. Called when worker returns 404 or
   * when the response.success === false.
   */
  function renderNotFound(container) {
    var card = container.querySelector('[data-collector-404]');
    if (card) card.removeAttribute('hidden');
    container.classList.add('is-not-found');
  }

  /**
   * Format an ISO last-login timestamp into "2 days ago", "today",
   * "5 weeks ago" etc. Returns "—" for null / unknown.
   */
  function formatRelativeTime(iso) {
    if (!iso) return '—';
    var then = new Date(iso);
    if (isNaN(then.getTime())) return '—';
    var diffMs = Date.now() - then.getTime();
    var diffSec = Math.max(0, Math.floor(diffMs / 1000));
    if (diffSec < 60) return 'just now';
    var diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return diffMin + 'm ago';
    var diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return diffHr + 'h ago';
    var diffDay = Math.floor(diffHr / 24);
    if (diffDay === 1) return 'yesterday';
    if (diffDay < 7) return diffDay + 'd ago';
    var diffWk = Math.floor(diffDay / 7);
    if (diffWk < 5) return diffWk + 'w ago';
    var diffMo = Math.floor(diffDay / 30);
    if (diffMo < 12) return diffMo + 'mo ago';
    var diffYr = Math.floor(diffDay / 365);
    return diffYr + 'y ago';
  }

  /* -------------- picks grid hydration -------------- */

  /**
   * Fetch /products/<handle>.js for one product handle. Returns a small
   * shape { title, image, url, price, vendor } or null on failure.
   * Public Shopify endpoint — no auth.
   */
  function fetchProductByHandle(handle) {
    return fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { 'Accept': 'application/json' }
    })
      .then(function (res) {
        if (!res.ok) throw new Error('product fetch failed: ' + res.status);
        return res.json();
      })
      .then(function (p) {
        return {
          title: p.title,
          image: p.featured_image || (p.images && p.images[0]) || '',
          url: p.url || '/products/' + handle,
          price: typeof p.price === 'number' ? (p.price / 100) : null,
          vendor: p.vendor || ''
        };
      })
      .catch(function () { return null; });
  }

  /**
   * Build a minimal product-card-style anchor for one pick. Doesn't try
   * to recreate Diffusion's full .#product-card markup — that requires
   * the snippet to be re-rendered server-side. Instead we render a
   * lightweight card that inherits the Top 8 cell sizing + hover
   * treatment via the parent .tbb-top8-item class.
   */
  function buildPickCard(productData) {
    var anchor = document.createElement('a');
    anchor.className = 'tbb-collector-pick-card';
    anchor.href = productData.url;
    anchor.setAttribute('aria-label', productData.title);

    var imgWrap = document.createElement('div');
    imgWrap.className = 'tbb-collector-pick-image-wrap';
    if (productData.image) {
      var img = document.createElement('img');
      img.src = productData.image;
      img.alt = productData.title;
      img.loading = 'lazy';
      img.decoding = 'async';
      imgWrap.appendChild(img);
    }
    anchor.appendChild(imgWrap);

    var titleEl = document.createElement('div');
    titleEl.className = 'tbb-collector-pick-title';
    titleEl.textContent = productData.title;
    anchor.appendChild(titleEl);

    if (productData.price !== null) {
      var priceEl = document.createElement('div');
      priceEl.className = 'tbb-collector-pick-price';
      priceEl.textContent = '$' + productData.price.toFixed(2);
      anchor.appendChild(priceEl);
    }

    return anchor;
  }

  /**
   * Hydrate the picks grid from the picks array. Fetches each product
   * in parallel; each cell either gets a card or is removed.
   */
  function hydratePicks(picks) {
    var grid = document.querySelector('[data-collector-picks-grid]');
    var emptyState = document.querySelector('[data-collector-picks-empty]');
    if (!grid) return;

    if (!Array.isArray(picks) || picks.length === 0) {
      // No picks — wipe skeleton, show empty-state copy
      var cells = grid.querySelectorAll('[data-collector-picks-cell]');
      for (var i = 0; i < cells.length; i++) cells[i].remove();
      if (emptyState) emptyState.removeAttribute('hidden');
      return;
    }

    // Cap at 8 (the grid only has 8 slots)
    var limited = picks.slice(0, 8);

    // Fire all product fetches in parallel
    var promises = limited.map(function (handle) {
      return fetchProductByHandle(handle).then(function (data) {
        return { handle: handle, data: data };
      });
    });

    Promise.all(promises).then(function (results) {
      var rendered = 0;
      for (var i = 0; i < results.length; i++) {
        var cell = grid.querySelector('[data-collector-picks-cell="' + (i + 1) + '"]');
        if (!cell) continue;
        var r = results[i];
        if (!r.data) {
          // Fetch failed — remove the cell quietly
          cell.remove();
          continue;
        }
        // Replace skeleton inside cell with hydrated card
        cell.classList.remove('is-skeleton');
        cell.textContent = '';
        cell.appendChild(buildPickCard(r.data));
        rendered++;
      }

      // Remove unused skeleton cells (collector has < 8 picks)
      for (var j = limited.length + 1; j <= 8; j++) {
        var unused = grid.querySelector('[data-collector-picks-cell="' + j + '"]');
        if (unused) unused.remove();
      }

      if (rendered === 0 && emptyState) emptyState.removeAttribute('hidden');
    });
  }

  /* -------------- header hydration -------------- */

  function initCollectorHeader(container) {
    if (container.dataset.tbbCollectorInit) return;
    container.dataset.tbbCollectorInit = '1';

    // Add body class so any future scoped overrides can target the page
    document.body.classList.add('tbb-collector-page');

    var workerUrl = container.dataset.workerUrl;
    var handle = getCollectorHandle();

    if (!workerUrl || !handle) {
      // Missing handle in URL — render not-found state immediately
      renderNotFound(container);
      return;
    }

    var fetchUrl = workerUrl.replace(/\/+$/, '') + '/collector/profile?u=' + encodeURIComponent(handle);

    // 5s timeout — same as seller pattern
    var controller = new AbortController();
    var timeoutId = setTimeout(function () { controller.abort(); }, 5000);

    fetch(fetchUrl, { signal: controller.signal })
      .then(function (res) {
        clearTimeout(timeoutId);
        if (res.status === 404) {
          renderNotFound(container);
          throw new Error('not-found');
        }
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (json) {
        if (!json.success || !json.data) {
          renderNotFound(container);
          return;
        }
        var d = json.data;

        // Avatar — DOM methods to keep API data XSS-safe
        var avatarEl = container.querySelector('[data-collector-avatar]');
        if (avatarEl && d.avatarUrl) {
          var img = document.createElement('img');
          img.src = d.avatarUrl;
          img.alt = d.displayName || '';
          img.width = 96;
          img.height = 96;
          img.loading = 'eager';
          avatarEl.textContent = '';
          avatarEl.appendChild(img);
        }

        // Display name
        var nameEl = container.querySelector('[data-collector-name]');
        if (nameEl && d.displayName) nameEl.textContent = d.displayName;

        // Bio
        var bioEl = container.querySelector('[data-collector-bio]');
        if (bioEl && d.bio) bioEl.textContent = d.bio;

        // Preset tags
        renderPresetTags(container.querySelector('[data-collector-tags]'), d.presetTags);

        // Location
        if (d.location) {
          var locText = container.querySelector('[data-collector-location-text]');
          if (locText) locText.textContent = d.location;
          var locWrap = container.querySelector('[data-collector-location]');
          if (locWrap) locWrap.removeAttribute('hidden');
        }

        // Member since
        if (d.memberSince) {
          var memText = container.querySelector('[data-collector-member-since-text]');
          if (memText) memText.textContent = d.memberSince;
          var memWrap = container.querySelector('[data-collector-member-since]');
          if (memWrap) memWrap.removeAttribute('hidden');
        }

        // Stats
        var stats = d.stats || {};
        var acquiredEl = container.querySelector('[data-collector-stat-acquired]');
        if (acquiredEl) acquiredEl.textContent = formatCount(stats.memoriesAcquired || 0);
        var archivedEl = container.querySelector('[data-collector-stat-archived]');
        if (archivedEl) archivedEl.textContent = formatCount(stats.memoriesArchived || 0);
        var lastLoginEl = container.querySelector('[data-collector-stat-last-login]');
        if (lastLoginEl) lastLoginEl.textContent = formatRelativeTime(stats.lastLogin);

        // Contact icons — collector channels (email, IG, TikTok, YouTube, web)
        var social = d.social || {};
        setContactChip(container, 'data-collector-contact-email',     safeUrl(social.email, 'mailto'));
        setContactChip(container, 'data-collector-contact-instagram', safeUrl(social.instagram, 'web'));
        setContactChip(container, 'data-collector-contact-tiktok',    safeUrl(social.tiktok, 'web'));
        setContactChip(container, 'data-collector-contact-youtube',   safeUrl(social.youtube, 'web'));
        setContactChip(container, 'data-collector-contact-website',   safeUrl(social.web, 'web'));

        // Mark hydrated for any CSS transitions
        container.classList.add('is-hydrated');

        // Hand off to picks hydration (same response payload — no second worker fetch)
        hydratePicks(d.picks);
      })
      .catch(function (err) {
        clearTimeout(timeoutId);
        if (err && err.message !== 'not-found') {
          console.warn('TBB collector profile fetch failed:', err.message);
        }
      });
  }

  function initAll() {
    var headers = document.querySelectorAll('[data-tbb-collector-header]');
    for (var i = 0; i < headers.length; i++) initCollectorHeader(headers[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor live preview
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) {
      var header = e.target.querySelector('[data-tbb-collector-header]');
      if (header) {
        header.removeAttribute('data-tbb-collector-init');
        initCollectorHeader(header);
      }
    });
  }
})();
